import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  CreateRoleRequest,
  GetInterviewResponse,
  GetRolesResponse,
  UpdateRoleRequest,
  UpdateRoleResponse,
} from '@pkg/types/api'

import api from '@/api'
import { INTERVIEW_KEY, useInterview } from '@/react-query/interview/interview'

const USER_ROLES_KEY = 'userRoles'
export const ROLE_KEY = 'role'

export const useUserRoles = () => {
  const queryClient = useQueryClient()

  return useQuery({
    queryKey: [USER_ROLES_KEY],
    queryFn: async () => {
      const roles = (await api.getUserRoles()).data.roles || []

      roles.forEach((role) => {
        queryClient.setQueryData([ROLE_KEY, role.id], role)
      })
      return roles
    },
  })
}

export const useRole = () => {
  const roleId = useInterview().data?.roleId

  return useQuery({
    queryKey: [ROLE_KEY, roleId],
    queryFn: async () => {
      if (!roleId) {
        return null
      }
      return (await api.getRole(roleId)).data.role
    },
    enabled: !!roleId,
  })
}

export const useCreateRole = () => {
  const queryClient = useQueryClient()
  const interviewId = useInterview().data?.id

  return useMutation({
    mutationFn: async (data: Omit<CreateRoleRequest, 'interviewId'>) => {
      if (!interviewId) {
        throw new Error('Interview id is not set')
      }

      const response = await api.createRole({
        role: data.role,
        interviewId,
      })

      const newData = response.data
      queryClient.setQueryData(
        [USER_ROLES_KEY],
        (oldRoles: GetRolesResponse['roles']) => [...oldRoles, newData.role],
      )
      queryClient.setQueryData([ROLE_KEY, newData.role.id], newData.role)
      queryClient.setQueryData(
        [INTERVIEW_KEY, interviewId],
        (
          oldData: GetInterviewResponse['interview'],
        ): GetInterviewResponse['interview'] => ({
          ...oldData,
          roleId: newData.role.id,
        }),
      )

      return response.data
    },
  })
}

export const useUpdateRole = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: UpdateRoleRequest) => {
      const response = await api.updateRole(data)
      return response.data
    },
    onMutate: (data) => {
      queryClient.setQueryData(
        [ROLE_KEY, data.role.id],
        (oldRole: UpdateRoleResponse['role']) => ({
          ...oldRole,
          ...data.role,
        }),
      )
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        [ROLE_KEY, data.role.id],
        (oldRole: UpdateRoleResponse['role']) => ({
          ...oldRole,
          ...data.role,
        }),
      )
    },
  })
}
